import { Box, Card, FormControlLabel, FormGroup, Stack, Typography } from "@mui/material";
import React, { useContext } from "react";
import { PageContainer } from "../../components/v2/PageContainer";
import { makeStyles } from "@mui/styles";
import { Theme } from "@mui/material/styles";
import { SwitchTextTrack } from "../../components/v2/utils/SwitchTextTrack";
import { UserContext } from "../../context/UserContext";
import { UserCtx } from "../../interfaces";
import { InfoToast } from "../../components/v2/utils/Toast";
import { generateGtmEvent } from "../../Helpers";

const useStyles = makeStyles<Theme, any>((theme) => ({
  title: {
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightBold,
    fontSize: (props) => "20px",
  },
  subHeading: {
    color: "#474747",
    fontSize: (props) => "16px",
  },
  actionButton: {
    color: theme.palette.text.secondary,
  },
}));

interface NotificationPageSettingsProps {
  isNotificationEnabled: boolean;
  isEmailNotificationEnabled: boolean;
}

export const NotificationSettingsPage = (props: NotificationPageSettingsProps) => {
  const classes = useStyles(props);
  const { isNotificationEnabled = true, isEmailNotificationEnabled = true } = props;
  const userCtx = useContext(UserContext) as UserCtx;

  const handleToggleNotifications = async (updatePayload: any) => {
    try {
      await userCtx.updateDbUser(userCtx.dbUser.id, updatePayload);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <PageContainer>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          margin: "0 auto",
          maxWidth: "800px",
        }}>
        <Typography variant="h1">Settings</Typography>
        <Box sx={{ width: "100%", marginBottom: "20px" }}>
          <Card
            sx={{
              border: "2px solid #DEDEDE",
              borderRadius: "15px",
              marginTop: "32px",
              padding: "24px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}>
            <Stack spacing={1}>
              <Typography variant="h1" className={classes.title}>
                Allow Notifications
              </Typography>
              <Typography className={classes.subHeading}>
                Receive real-time updates and alerts directly within Digit.
              </Typography>
            </Stack>

            <FormGroup>
              <FormControlLabel
                aria-label="position"
                sx={{ display: "hidden", marginRight: "0px" }}
                control={
                  <SwitchTextTrack
                    name="isNotificationEnabled"
                    role="presentation"
                    inputProps={{ "aria-label": "controlled" }}
                    value={isNotificationEnabled}
                    checked={isNotificationEnabled}
                    onChange={async (event) => {
                      await handleToggleNotifications({
                        notifications_enabled: event.target.checked,
                      });
                      InfoToast(
                        "Your notifications are turned " +
                          (event.target.checked ? "on" : "off")
                      );
                      generateGtmEvent({
                        event: "select",
                        action: event.target.checked ? "click" : "unclick",
                        name: "onclick",
                        type: "checkbox",
                        region: "main content",
                        section: "settings",
                        text: "allow notifications",
                      });
                    }}
                  />
                }
                label=""
              />
            </FormGroup>
          </Card>

          <Card
            sx={{
              border: "2px solid #DEDEDE",
              borderRadius: "15px",
              marginTop: "24px",
              padding: "24px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}>
            <Stack spacing={1}>
              <Typography variant="h1" className={classes.title}>
                Email subscriptions
              </Typography>
              <Typography className={classes.subHeading}>
                Get important updates and reminders sent to your email inbox.
              </Typography>
            </Stack>
            <FormGroup>
              <FormControlLabel
                aria-label="position"
                sx={{ display: "hidden", marginRight: "0px" }}
                control={
                  <SwitchTextTrack
                    inputProps={{ "aria-label": "controlled" }}
                    name="isEmailNotificationEnabled"
                    role="presentation"
                    value={isEmailNotificationEnabled}
                    onChange={async (event) => {
                      await handleToggleNotifications({
                        email_notifications_enabled: event.target.checked,
                      });
                      InfoToast(
                        "You have " +
                          (event.target.checked ? "subscribed" : "unsubscribed") +
                          " from email notifications"
                      );
                      generateGtmEvent({
                        event: "select",
                        action: event.target.checked ? "click" : "unclick",
                        name: "onclick",
                        type: "checkbox",
                        region: "main content",
                        section: "settings",
                        text: "email subscriptions",
                      });
                    }}
                    checked={isEmailNotificationEnabled}
                  />
                }
                label=""
              />
            </FormGroup>
          </Card>
        </Box>
      </Box>
    </PageContainer>
  );
};
